import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Keep one portal container and move it between the page and dialog. This
// preserves the WebGL canvas, camera, and measurement state without loading
// a second copy of a potentially large model.
export default function ReconstructionMeasurementWorkspace({ open, loading, onOpen, onClose, downloadUrl, onCreateAnother, children }) {
  const inlineHost = useRef(null), modalHost = useRef(null), dialog = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [container] = useState(() => typeof document === 'undefined' ? null : document.createElement('div'));

  useLayoutEffect(() => {
    if (!container || !inlineHost.current) return;
    if (open) {
      inlineHost.current.style.minHeight = `${inlineHost.current.getBoundingClientRect().height}px`;
      modalHost.current.appendChild(container);
    } else {
      inlineHost.current.appendChild(container);
      inlineHost.current.style.minHeight = '';
    }
  }, [open, container]);

  useEffect(() => () => { if (container) container.remove(); }, [container]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current.focus({ preventScroll: true });
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
      } else if (event.key === 'Tab') {
        const focusable = Array.from(dialog.current.querySelectorAll(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )).filter(element => element.getClientRects().length > 0);
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (!first) { event.preventDefault(); return; }
        if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !focusable.includes(document.activeElement))) {
          event.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = previousOverflow;
      if (previousFocus && previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [open]);

  return <>
    <div ref={inlineHost} />
    <div role="group" aria-label="3D model actions" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
      <button type="button" className="btn radius" style={{ flex: '1 1 220px', margin: 0 }} disabled={loading} onClick={onOpen} aria-haspopup="dialog" aria-expanded={open}>
        <i className="bx bx-ruler" aria-hidden="true" /> Measure Distances
      </button>
      <a className="btn btn-success radius" style={{ flex: '1 1 220px', margin: 0 }} href={downloadUrl} target="_blank" rel="noopener noreferrer" download>
        <i className="bx bx-download" aria-hidden="true" /> Download GLB
      </a>
      <button type="button" className="btn btn-warning radius" style={{ flex: '1 1 220px', margin: 0 }} onClick={onCreateAnother}>
        <i className="bx bx-refresh" aria-hidden="true" /> Create Another
      </button>
    </div>
    {container && createPortal(children, container)}
    {open && createPortal(
      <div ref={dialog} role="dialog" aria-modal="true" aria-label="Measure 3D distances and areas" tabIndex={-1} className="reconstruction-measure-dialog"
        style={{ position: 'fixed', inset: 0, zIndex: 11000, background: '#161c25', color: '#fff', padding: 16, overflowY: 'auto' }}>
        <style>{`
          .reconstruction-measure-dialog, .reconstruction-measure-dialog * { box-sizing: border-box; }
          .reconstruction-measure-dialog label, .reconstruction-measure-dialog legend,
          .reconstruction-measure-dialog th, .reconstruction-measure-dialog td { color: #fff; }
          .reconstruction-measure-dialog .inactive-text { color: #c9d1d9; }
          .reconstruction-measure-dialog fieldset { border-color: #394655; }
          .reconstruction-measure-dialog legend { background: #161c25; }
          .reconstruction-measure-dialog input[type="number"], .reconstruction-measure-dialog select {
            background: #f4f6f8; color: #172033; border-color: #526273;
          }
          .reconstruction-measure-dialog .btn-secondary { background: #334155; color: #fff; border-color: #526273; }
          .reconstruction-measure-dialog .btn-secondary:hover { background: #475569; }
          .reconstruction-measure-dialog table, .reconstruction-measure-dialog thead,
          .reconstruction-measure-dialog tbody, .reconstruction-measure-dialog tr,
          .reconstruction-measure-dialog tbody tr:nth-child(even),
          .reconstruction-measure-dialog tbody tr:nth-child(odd) { background: transparent; color: #fff; }
          .reconstruction-measure-dialog th, .reconstruction-measure-dialog td { border-color: #394655; }
        `}</style>
        <div style={{ maxWidth: 1400, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, position: 'sticky', top: -16, background: '#161c25', zIndex: 5, padding: '12px 0', marginBottom: 8 }}>
            <h2 style={{ margin: 0, fontSize: 20, color: '#fff' }}>Measure 3D Distances and Areas</h2>
            <button type="button" className="btn btn-secondary radius" style={{ margin: 0 }} onClick={onClose}>
              <i className="bx bx-x" aria-hidden="true" /> Close
            </button>
          </div>
          <div ref={modalHost} />
        </div>
      </div>, document.body
    )}
  </>;
}
